import {
	Popover,
	PopoverTrigger,
	PopoverContent,
	Input,
	Listbox,
	ListboxSection,
	ListboxItem,
	Chip,
	Button,
} from '@heroui/react';
import { useState, useMemo, Key, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from '../../../../core/rootHooks';
import {
	selectFilterSearchFormData,
	selectIsFetchedFilters,
	selectIsFetchedPortfolios,
	selectPortfolioSearchFormData,
	selectSubscribedOwnershipSearchFormData,
	selectSubscribedSectorSearchFormData,
	selectSubscribedVesselSearchFormData,
} from '../../../search/searchSelectors';
import { AppDispatch } from '../../../../core/store';
import { getFiltersRequest } from '../../../filter/filterSlice';
import { getPortfoliosRequest } from '../../../portfolio/portfolioSlice';

export type CohortSourceType = 'auto' | 'filter' | 'portfolio' | 'sector' | 'owner' | 'vessel';

export interface ICohortSourceSelection {
	type: CohortSourceType;
	key: string;
	label: string;
}

export const AUTO_COHORT_SOURCE: ICohortSourceSelection = {
	type: 'auto',
	key: 'auto',
	label: 'Auto (Similar Size & Age)',
};

interface ICohortSourceSelectProps {
	selected: ICohortSourceSelection;
	onChange: (selection: ICohortSourceSelection) => void;
}

const CohortSourceSelect: React.FC<ICohortSourceSelectProps> = ({ selected, onChange }) => {
	const dispatch = useAppDispatch<AppDispatch>();

	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');

	const portfoliosReady = useAppSelector(selectIsFetchedPortfolios);
	const filtersReady = useAppSelector(selectIsFetchedFilters);
	
	const vessels = useAppSelector(selectSubscribedVesselSearchFormData);
	const filters = useAppSelector(selectFilterSearchFormData);
	const portfolios = useAppSelector(selectPortfolioSearchFormData);
	const sectors = useAppSelector(selectSubscribedSectorSearchFormData);
	const owners = useAppSelector(selectSubscribedOwnershipSearchFormData);

	const term = searchTerm.toLowerCase();

	useEffect(() => {
		if (!portfoliosReady) {
			dispatch(getPortfoliosRequest());
		}
		if (!filtersReady) {
			dispatch(getFiltersRequest());
		}
	}, [portfoliosReady, filtersReady, dispatch]);

	const filteredFilters = useMemo(
		() =>
			// term.length >= 3 &&
			filters?.length
				? filters
						.filter((f) => f.name.toLowerCase().includes(term))
						.sort((a, b) => (a.isOwnFilter ? -1 : 1) || a.name.localeCompare(b.name))
				: [],
		[filters, term],
	);

	const filteredPortfolios = useMemo(
		() =>
			// term.length >= 3 &&
			portfolios?.length
				? portfolios
						.filter((p) => p.name.toLowerCase().includes(term))
						.sort((a, b) => (a.isOwnPortfolio ? -1 : 1) || a.name.localeCompare(b.name))
				: [],
		[portfolios, term],
	);

	const filteredOwners = useMemo(
		() =>
			term.length >= 3 && owners?.length
				? owners.filter((o) => o.name.toLowerCase().includes(term)).sort((a, b) => a.name.localeCompare(b.name))
				: [],
		[owners, term],
	);

	const filteredSectors = useMemo(
		() =>
			term.length >= 3 && sectors?.length
				? sectors.filter((s) => s.name.toLowerCase().includes(term)).sort((a, b) => a.name.localeCompare(b.name))
				: [],
		[sectors, term],
	);

	const filteredVessels = useMemo(
		() =>
			term.length >= 3 && vessels?.length
				? vessels.filter((v) => v.name.toLowerCase().includes(term) || v.imoNumber.toString().includes(term))
				: [],
		[vessels, term],
	);

	const handleSelect = (key: Key) => {
		const keyStr = key.toString();
		if (keyStr === 'auto') {
			onChange(AUTO_COHORT_SOURCE);
		} else if (keyStr.startsWith('sector-')) {
			const name = keyStr.slice('sector-'.length);
			onChange({ type: 'sector', key: keyStr, label: name });
		} else if (keyStr.startsWith('portfolio-')) {
			const id = parseInt(keyStr.slice('portfolio-'.length));
			const portfolio = portfolios.find((p) => p.id === id);
			onChange({ type: 'portfolio', key: keyStr, label: portfolio?.name ?? '' });
		} else if (keyStr.startsWith('filter-')) {
			const id = parseInt(keyStr.slice('filter-'.length));
			const filter = filters.find((f) => f.id === id);
			onChange({ type: 'filter', key: keyStr, label: filter?.name ?? '' });
		} else if (keyStr.startsWith('owner-')) {
			const name = keyStr.slice('owner-'.length);
			onChange({ type: 'owner', key: keyStr, label: name });
		} else if (keyStr.startsWith('vessel-')) {
			const imoNumber = parseInt(keyStr.slice('vessel-'.length));
			const vessel = vessels?.find((v) => v.imoNumber === imoNumber);
			onChange({ type: 'vessel', key: keyStr, label: vessel ? `${vessel.imoNumber} - ${vessel.name}` : '' });
		}

		setIsOpen(false);
		setSearchTerm('');
	};

	return (
		<div className="flex flex-row items-center gap-2 justify-center pb-1 border-b border-msi-darkgrey-color-300 bg-msi-white-color">
			<span className="text-small font-medium text-msi-midblue-color-500 whitespace-nowrap">Cohort Source:</span>
			<Popover
				isOpen={isOpen}
				onOpenChange={(open) => {
					setIsOpen(open);
					if (!open) setSearchTerm('');
				}}
				placement="bottom-start"
				showArrow={false}
				classNames={{ content: 'p-0 rounded border border-msi-darkgrey-color-300 shadow-md' }}
			>
				<PopoverTrigger>
					<Button
						size="md"
						variant="bordered"
						aria-label="Cohort Source"
						endContent={<FontAwesomeIcon icon={faChevronDown} className="text-[9px] text-msi-darkgrey-color" />}
						className="h-8 min-w-98 max-w-xs rounded border border-msi-darkgrey-color-300 bg-msi-white-color justify-between"
					>
						<span className="text-small text-msi-darkblue-color truncate">{selected.label}</span>
					</Button>
				</PopoverTrigger>

				<PopoverContent className="w-96 p-0">
					<div className="flex flex-col w-full">
						<div className="p-2 border-b border-msi-darkgrey-color-300">
							<Input
								autoFocus
								size="sm"
								placeholder="Search... (Filter, Portfolio, Vessel, Sector, Owner)"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								isClearable
								onClear={() => setSearchTerm('')}
								startContent={<FontAwesomeIcon icon={faSearch} className="text-[10px] text-msi-darkgrey-color" />}
								classNames={{
									inputWrapper: 'h-7 rounded border border-msi-darkgrey-color-300 shadow-none bg-msi-white-color',
									input: 'text-xs',
								}}
							/>
						</div>
						<Listbox
							aria-label="Select cohort source"
							onAction={handleSelect}
							selectedKeys={[selected.key]}
							classNames={{ list: 'max-h-64 overflow-y-auto scrollbar-thin' }}
						>
							<ListboxSection title="Default" showDivider className="pr-2">
								<ListboxItem key="auto" textValue="Auto (Similar Size & Age)" className="rounded h-6">
									<span className="text-xs">Auto (Similar Size &amp; Age)</span>
								</ListboxItem>
							</ListboxSection>

							{/* Filters */}
							<ListboxSection title="Filters" showDivider hidden={filteredFilters.length === 0} className="pr-2">
								{filteredFilters.length > 0 ? (
									filteredFilters.map((f) => (
										<ListboxItem
											key={`filter-${f.id}`}
											textValue={f.name}
											className="rounded h-6"
											classNames={{ title: 'flex justify-between items-center' }}
										>
											<span className="text-xs truncate flex-1">{f.name}</span>
											<Chip
												className={`flex-none text-[10px] rounded h-4 ${f.isOwnFilter ? 'bg-success-500' : 'bg-warning-500'} text-msi-white-color ml-2`}
												size="sm"
											>
												{f.isOwnFilter ? 'Personal' : 'Team'}
											</Chip>
										</ListboxItem>
									))
								) : (
									<ListboxItem key="filter-placeholder" isReadOnly hideSelectedIcon textValue="" className="hidden" />
								)}
							</ListboxSection>

							{/* Portfolios */}
							<ListboxSection title="Portfolios" showDivider hidden={filteredPortfolios.length === 0} className="pr-2">
								{filteredPortfolios.length > 0 ? (
									filteredPortfolios.map((p) => (
										<ListboxItem
											key={`portfolio-${p.id}`}
											textValue={p.name}
											className="rounded h-6"
											classNames={{ title: 'flex justify-between items-center' }}
										>
											<span className="text-xs truncate flex-1">{p.name}</span>
											<Chip
												className={`flex-none text-[10px] rounded h-4 ${p.isOwnPortfolio ? 'bg-success-500' : 'bg-warning-500'} text-msi-white-color ml-2`}
												size="sm"
											>
												{p.isOwnPortfolio ? 'Personal' : 'Team'}
											</Chip>
										</ListboxItem>
									))
								) : (
									<ListboxItem key="portfolio-placeholder" isReadOnly hideSelectedIcon textValue="" className="hidden" />
								)}
							</ListboxSection>

							{/* Vessels */}
							<ListboxSection title="Vessels" showDivider hidden={searchTerm.length === 0} className="pr-2">
								{searchTerm.length < 3 ? (
									<ListboxItem key="vessel-hint" isReadOnly hideSelectedIcon textValue="hint" className="rounded h-6">
										<span className="text-xs text-msi-darkgrey-color italic">
											Type at least 3 characters to search vessels...
										</span>
									</ListboxItem>
								) : filteredVessels.length > 0 ? (
									filteredVessels.map((v) => (
										<ListboxItem
											key={`vessel-${v.imoNumber}`}
											textValue={`${v.imoNumber} - ${v.name}`}
											className="rounded h-6"
											classNames={{ title: 'flex justify-between items-center' }}
										>
											<span className="text-xs truncate flex-1">
												{v.imoNumber} - {v.name}
											</span>
											<Chip
												className="flex-none text-[10px] rounded h-4 bg-msi-midblue-color-600 text-msi-white-color ml-2"
												size="sm"
											>
												{v.sector}
											</Chip>
										</ListboxItem>
									))
								) : (
									<ListboxItem
										key="vessel-not-found"
										isReadOnly
										hideSelectedIcon
										textValue="No vessels found"
										className="rounded h-6"
									>
										<span className="text-xs text-msi-darkgrey-color">No vessels found</span>
									</ListboxItem>
								)}
							</ListboxSection>

							{/* Ownerships */}
							<ListboxSection title="Ownerships" showDivider hidden={filteredOwners.length === 0} className="pr-2">
								{filteredOwners.length > 0 ? (
									filteredOwners.map((o) => (
										<ListboxItem
											key={`owner-${o.name}`}
											textValue={o.name}
											className="rounded h-6"
											classNames={{ title: 'flex justify-between items-center' }}
										>
											<span className="text-xs truncate flex-1">{o.name}</span>
											<Chip
												className="flex-none text-[10px] rounded h-4 bg-msi-orange-color-600 text-msi-white-color ml-2"
												size="sm"
											>
												Company
											</Chip>
										</ListboxItem>
									))
								) : (
									<ListboxItem key="owner-placeholder" isReadOnly hideSelectedIcon textValue="" className="hidden" />
								)}
							</ListboxSection>

							{/* Sectors */}
							<ListboxSection title="Sectors" showDivider hidden={filteredSectors.length === 0} className="pr-2">
								{filteredSectors.length > 0 ? (
									filteredSectors.map((s) => (
										<ListboxItem
											key={`sector-${s.name}`}
											textValue={s.name}
											className="rounded h-6"
											classNames={{ title: 'flex justify-between items-center' }}
										>
											<span className="text-xs truncate flex-1">{s.name}</span>
											<Chip
												className="flex-none text-[10px] rounded h-4 bg-msi-darkblue-color-600 text-msi-white-color ml-2"
												size="sm"
											>
												Sector
											</Chip>
										</ListboxItem>
									))
								) : (
									<ListboxItem key="sector-placeholder" isReadOnly hideSelectedIcon textValue="" className="hidden" />
								)}
							</ListboxSection>
						</Listbox>
					</div>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export default CohortSourceSelect;
